import React from "react";
import { Reveal } from "@/components/Reveal";
import { HeroLogo, DegreeRing } from "@/components/HeroLogo";

const SectionLabel = ({ children, tone = "dark" }) => (
  <div className="flex items-center gap-4 mb-8 sm:mb-10">
    <span
      className={`font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase ${
        tone === "dark" ? "hoa-gold-text" : "hoa-gold-text-deep"
      }`}
    >
      {children}
    </span>
    <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
  </div>
);

const FramedImage = ({ src, alt, ratio = "aspect-[4/3]" }) => (
  <div className="relative bg-[var(--hoa-cream-50)] rounded-[var(--hoa-radius-lg)] shadow-[var(--hoa-shadow-card)] p-3">
    <div className="absolute inset-3 rounded-[calc(var(--hoa-radius-lg)-10px)] border border-[rgba(179,145,74,0.4)] pointer-events-none z-[2]" />
    <div className={`${ratio} overflow-hidden rounded-[calc(var(--hoa-radius-lg)-10px)]`}>
      <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    </div>
  </div>
);

/* ------------------------------------------------------------------ 01 */
export const TheHouse = () => (
  <section id="story" data-testid="story-house-section" className="hoa-texture-cream hoa-grain">
    <div className="relative z-[1] mx-auto w-full max-w-[1120px] px-5 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <SectionLabel tone="light">01 — The House</SectionLabel>
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <Reveal>
            <h2 className="font-display text-[var(--hoa-ink-950)] text-4xl sm:text-5xl leading-[1.05]">
              Not a bakery.
              <br />
              <span className="hoa-gold-text-deep">A home.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-body text-[rgba(11,12,13,0.72)] text-base sm:text-lg leading-[1.85] mt-7 max-w-xl">
              House of Appetite began where every good thing begins — in a home kitchen, in small
              batches, with complete attention.
            </p>
            <p className="font-body text-[rgba(11,12,13,0.72)] text-base sm:text-lg leading-[1.85] mt-5 max-w-xl">
              Nothing here waits on a shelf. Nothing is made by the thousand. Each order is baked
              fresh, with time taken and corners never cut. This is baking as it was always meant to
              be — premium, personal, unhurried.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-4">
              {[
                "Small batches, baked to order — never stock, always fresh",
                "A hygiene-first kitchen, spotless to an obsessive standard",
                "Premium ingredients only — if it is not the best, it is not baked",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-[0.55em] w-2 h-2 rounded-full border-[1.5px] border-[#8A6D33] shrink-0" />
                  <span className="font-body text-[rgba(11,12,13,0.78)] text-sm sm:text-base leading-[1.7]">{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="font-display italic hoa-gold-text-deep text-xl sm:text-2xl mt-9">“baked with warmth of love.”</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <FramedImage
            src="/images/house-spread-v2.jpg"
            alt="A spread of House of Appetite bakes — loaves, cheesecake, brownies and desserts"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ 02 */
export const TheMark = () => (
  <section data-testid="story-mark-section" className="hoa-texture hoa-grain">
    <div className="relative z-[1] mx-auto w-full max-w-[1120px] px-5 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <SectionLabel>02 — The Mark</SectionLabel>
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className="order-2 lg:order-1 flex justify-center">
          <div className="relative py-10 px-14">
            <div className="absolute inset-0 border border-[rgba(179,145,74,0.3)] rounded-[6px]" />
            <HeroLogo fontSize="clamp(5rem, 14vw, 7rem)" />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <h2 className="font-display text-[var(--hoa-cream-50)] text-4xl sm:text-5xl leading-[1.05]">
              appetite, <span className="hoa-gold-text">at the right degree</span>
              <span className="inline-block -translate-y-[0.55em]"><DegreeRing size="0.32em" /></span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-body text-[rgba(240,233,216,0.75)] text-base sm:text-lg leading-[1.85] mt-7 max-w-xl">
              Our mark is a single letter you could almost taste. The small “a” is appetite itself —
              spoken softly, the way indulgence should be. And where the world puts a full stop, we
              place a degree.
            </p>
            <p className="font-body text-[rgba(240,233,216,0.75)] text-base sm:text-lg leading-[1.85] mt-5 max-w-xl">
              At 180°, flour and butter stop being ingredients and start being memory. So our name
              carries its own oven — appetite, held at exactly the right heat.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="grid sm:grid-cols-2 gap-4 mt-9">
              <div className="border border-[rgba(179,145,74,0.3)] rounded-[var(--hoa-radius-md)] p-5 bg-[rgba(240,233,216,0.04)]">
                <p className="font-label text-[10px] tracking-[0.28em] uppercase hoa-gold-text">The Degree</p>
                <p className="font-body text-[rgba(240,233,216,0.72)] text-sm leading-[1.7] mt-3">
                  The universal mark of the oven. 180° — where butter browns, sugar sings, and patience becomes flavour.
                </p>
              </div>
              <div className="border border-[rgba(179,145,74,0.3)] rounded-[var(--hoa-radius-md)] p-5 bg-[rgba(240,233,216,0.04)]">
                <p className="font-label text-[10px] tracking-[0.28em] uppercase hoa-gold-text">The Appetite</p>
                <p className="font-body text-[rgba(240,233,216,0.72)] text-sm leading-[1.7] mt-3">
                  Lowercase, and on purpose — a note from our kitchen to yours, not a sign above a shop.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ 03 */
export const TheCurator = () => (
  <section data-testid="story-curator-section" className="hoa-texture-cream hoa-grain">
    <div className="relative z-[1] mx-auto w-full max-w-[1120px] px-5 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <SectionLabel tone="light">03 — The Curator</SectionLabel>
      </Reveal>
      <div className="grid lg:grid-cols-[280px_1fr] gap-14 lg:gap-16 items-center">
        <Reveal className="flex justify-center lg:justify-start">
          <div className="relative w-[180px] sm:w-[210px]" data-testid="curator-portrait">
            {/* concentric double frame — thick gold arch, aligned with the photo */}
            <div className="rounded-t-full rounded-b-[18px] border-[3px] border-[#B3914A] p-1.5 shadow-[var(--hoa-shadow-card)] bg-[var(--hoa-cream-50)]">
              <div className="overflow-hidden rounded-t-full rounded-b-[12px]">
                <img
                  src="/images/divya.jpg"
                  alt="Divya Galada, curator of house of appetite"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </Reveal>
        <div className="text-center lg:text-left">
          <Reveal>
            <h2 className="font-display text-[var(--hoa-ink-950)] text-4xl sm:text-5xl leading-[1.08]">
              <span className="block text-xl sm:text-2xl text-[rgba(11,12,13,0.6)]">Curated by</span>
              <span className="hoa-gold-text-deep block mt-2">Divya Galada</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-body text-[rgba(11,12,13,0.72)] text-base sm:text-lg leading-[1.85] mt-8 max-w-xl mx-auto lg:mx-0">
              Fifteen years of baking teach a person three things: patience, precision, and the
              weight of a promise kept.
            </p>
            <p className="font-body text-[rgba(11,12,13,0.72)] text-base sm:text-lg leading-[1.85] mt-5 max-w-xl mx-auto lg:mx-0">
              House of Appetite is those years, folded into every recipe. Each one is tested to the
              gram. Every batch is measured, timed and tasted. Every box that leaves the house is a
              commitment honoured.
            </p>
            <p className="font-body text-[rgba(11,12,13,0.8)] text-base sm:text-lg leading-[1.85] mt-5 max-w-xl mx-auto lg:mx-0">
              Fifteen years of practice. A lifetime of appetite. That is the curation behind every crumb.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex items-center justify-center lg:justify-start gap-4 mt-10">
              <span className="w-10 h-px bg-[rgba(179,145,74,0.5)]" />
              <p className="font-display italic hoa-gold-text-deep text-lg sm:text-xl">
                “I measure twice, bake once, and never break a promise.”
              </p>
              <span className="w-10 h-px bg-[rgba(179,145,74,0.5)] hidden sm:block" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ 05 */
export const ThePromise = () => (
  <section data-testid="story-promise-section" className="hoa-texture-cream hoa-grain">
    <div className="relative z-[1] mx-auto w-full max-w-[1120px] px-5 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <SectionLabel tone="light">05 — The Promise</SectionLabel>
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <Reveal>
          <FramedImage
            src="/images/apron.jpg"
            alt="House of Appetite apron with the a° brand mark"
          />
        </Reveal>
        <div>
          <Reveal>
            <h2 className="font-display text-[var(--hoa-ink-950)] text-4xl sm:text-5xl leading-[1.05]">
              Worn in our kitchen.
              <br />
              <span className="hoa-gold-text-deep">Kept as our word.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-body text-[rgba(11,12,13,0.72)] text-base sm:text-lg leading-[1.85] mt-7 max-w-xl">
              The apron is where the brand lives — not on a billboard, but tied twice around a
              promise: that whatever carries the a° was made slowly, cleanly, honestly, and for you
              alone.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-display text-[var(--hoa-ink-950)] text-2xl sm:text-3xl mt-9 leading-snug">
              Slow by choice. Honest by habit. <span className="hoa-gold-text-deep">Yours by heart.</span>
            </p>
            <p className="font-display italic hoa-gold-text-deep text-xl mt-6">give in.</p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
